import { Dispatch, memo, SetStateAction, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  ExternalLink,
  Check,
  FileVideo,
  Pencil,
  Play,
  Star,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import Modal from "@/components/ui/Modal";
import VideoPlayerV3, { DEFAULT_BLURHASH } from "@/components/ui/VideoPlayerV3";
import VideoUploaderWithPreview from "@/components/ui/VideoUploaderWithPreview";
import { useUpdateCreatorMutation } from "@/redux/feature/user/userApi";
import { updateUser } from "@/redux/feature/user/userSlice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import type { FileDetails, PortfolioVideoItem, TUser } from "@/types/types";
import type { TProfileVideo } from "@/redux/feature/user/userType";

type PortfolioPreviewItem = PortfolioVideoItem & {
  isPrimary?: boolean;
  isLegacy?: boolean;
  normalizedId: string;
  playableUrl: string;
  thumbnailUrl: string;
  blurhash: string;
  title: string;
};

type ManagedPortfolioVideoItem = PortfolioVideoItem & {
  isPrimary?: boolean;
  thumbnail?: string | null;
  videoThumbnail?: string | null;
};

type LegacyPortfolioVideoItem = ManagedPortfolioVideoItem & {
  isLegacy?: boolean;
};

const safeUrl = (url?: string) => {
  const value = (url || "").trim();
  if (!value) return "";
  if (!/^https?:\/\//i.test(value)) return `https://${value}`;
  return value;
};

const isValidPortfolioVideo = (video: ManagedPortfolioVideoItem) => {
  return Boolean(
    video?.file?.live_url ||
      video?.file?.local_url ||
      video?.linkUrl ||
      video?.brandName?.trim() ||
      video?.description?.trim(),
  );
};

const getVideoId = (video: ManagedPortfolioVideoItem, index: number) =>
  video.id ||
  (video.file as any)?.key ||
  video.file?.live_url ||
  video.file?.local_url ||
  `portfolio-${index}`;

const getFileUrl = (file?: FileDetails) =>
  file?.live_url || file?.local_url || "";

const getVideoIdentity = (video: ManagedPortfolioVideoItem, index = 0) => {
  const isUpload = video.sourceType !== "link";
  const source = isUpload ? getFileUrl(video.file) : safeUrl(video.linkUrl);

  return (source || getVideoId(video, index)).trim().toLowerCase();
};

const getThumbnailUrl = (video: ManagedPortfolioVideoItem) =>
  video.file?.thumbnail ||
  video.thumbnail ||
  video.videoThumbnail ||
  (video.file as any)?.thumbnailUrl ||
  (video.file as any)?.poster ||
  "";

const toLegacyPortfolioVideo = (
  item: TProfileVideo,
  index: number,
): LegacyPortfolioVideoItem => ({
  id: (item.video as any)?.key || item.video?.live_url || `legacy-${index}`,
  brandName: "",
  isPersonalProject: false,
  description: "",
  sourceType: "upload",
  file: {
    ...item.video,
    thumbnail: (item.video as any)?.thumbnail || item.thumbnail || null,
    hash: (item.video as any)?.hash || item.hash || null,
  },
  thumbnail: item.thumbnail,
  createdAt: "",
  isPrimary: item.isActive,
  isLegacy: true,
});

const normalizeVideos = (videos: ManagedPortfolioVideoItem[] = []) => {
  const seen = new Set<string>();

  return videos
    .filter(isValidPortfolioVideo)
    .reduce<PortfolioPreviewItem[]>((acc, video, index) => {
      const normalizedId = getVideoId(video, index);
      const isUpload = video.sourceType !== "link";
      const playableUrl = isUpload
        ? getFileUrl(video.file)
        : safeUrl(video.linkUrl);
      const identity = (playableUrl || normalizedId).trim().toLowerCase();
      if (seen.has(identity)) return acc;
      seen.add(identity);
      const thumbnailUrl = isUpload ? getThumbnailUrl(video) : "";

      acc.push({
        ...video,
        sourceType: isUpload ? "upload" : "link",
        normalizedId,
        playableUrl,
        thumbnailUrl,
        blurhash: isUpload
          ? video.file?.hash || DEFAULT_BLURHASH
          : DEFAULT_BLURHASH,
        title:
          video.brandName?.trim() ||
          (video.isPersonalProject
            ? "Personal Project"
            : `Portfolio ${acc.length + 1}`),
      });

      return acc;
    }, []);
};

const getCountLabel = (count: number) => {
  if (count === 0) return "No videos";
  if (count === 1) return "1 Video";
  return `${count} Videos`;
};

const getPrimaryVideo = (videos: PortfolioPreviewItem[]) => {
  return videos.find((video) => video.isPrimary) || videos[0] || null;
};

const getPortfolioVideos = (user?: TUser) => {
  const portfolioVideos = (user?.portfolioVideos ??
    []) as ManagedPortfolioVideoItem[];
  const legacyVideos = (user?.videos ?? [])
    .filter((item) => item?.video?.live_url || item?.video?.local_url)
    .map(toLegacyPortfolioVideo);

  return normalizeVideos([...portfolioVideos, ...legacyVideos]);
};

const toPersistablePortfolioVideo = ({
  normalizedId,
  playableUrl,
  thumbnailUrl,
  blurhash,
  title,
  isLegacy,
  ...video
}: PortfolioPreviewItem): ManagedPortfolioVideoItem => video;

const formatDate = (date?: string) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString();
};

const PlayPreview = ({
  item,
  playingUrl,
  onPlay,
}: {
  item: PortfolioPreviewItem;
  playingUrl: string;
  onPlay: (item: PortfolioPreviewItem) => void;
}) => {
  const isUpload = item.sourceType !== "link";
  const isPlaying = playingUrl === item.playableUrl;

  if (!isUpload) {
    return (
      <button
        type="button"
        onClick={() => onPlay(item)}
        className="relative flex h-full w-full flex-col items-center justify-center gap-2 bg-[#F8FAFC] text-center text-[#667085]"
      >
        <ExternalLink className="size-5 text-[#FF6600]" />
        <span className="px-2 text-[11px] font-semibold">Open video link</span>
      </button>
    );
  }

  return (
    <div className="relative h-full w-full bg-black">
      {isPlaying ? (
        <VideoPlayerV3
          url={item.playableUrl}
          thumbnailUrl={item.thumbnailUrl}
          playing
          muted={false}
          blurhash={item.blurhash}
          onPlay={() => onPlay(item)}
          border="rounded-none"
        />
      ) : item.thumbnailUrl ? (
        <img
          src={item.thumbnailUrl}
          alt={item.title}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="relative h-full w-full bg-[#101828]">
          {item.playableUrl ? (
            <video
              src={item.playableUrl}
              preload="metadata"
              muted
              playsInline
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <FileVideo className="size-8 text-white/70" />
            </div>
          )}
        </div>
      )}

      {!isPlaying && (
        <button
          type="button"
          onClick={() => onPlay(item)}
          className="absolute inset-0 flex items-center justify-center bg-black/10"
          aria-label="Play portfolio video"
        >
          <span className="flex size-12 items-center justify-center rounded-full bg-white/95 text-[#FF6600] shadow-sm">
            <Play className="ml-0.5 size-5 fill-current" />
          </span>
        </button>
      )}
    </div>
  );
};

const PortfolioVideoCard = ({
  item,
  playingUrl,
  onPlay,
}: {
  item: PortfolioPreviewItem;
  playingUrl: string;
  onPlay: (item: PortfolioPreviewItem) => void;
}) => (
  <article className="min-w-0">
    <div className="relative aspect-[9/16] overflow-hidden rounded-lg bg-black shadow-sm ring-1 ring-[#EAECF0]">
      <PlayPreview item={item} playingUrl={playingUrl} onPlay={onPlay} />
      {item.isPrimary && (
        <span className="absolute left-2 top-2 rounded-full bg-[#ECFDF3] px-2 py-0.5 text-[11px] font-semibold text-[#027A48] shadow-sm">
          Primary
        </span>
      )}
    </div>
    <div className="pt-3">
      <h6 className="truncate text-sm font-semibold text-[#101828]">
        {item.title}
      </h6>
      <p className="mt-1 line-clamp-2 text-xs leading-5 text-[#667085]">
        {item.description || "No description added yet."}
      </p>
    </div>
  </article>
);
const ConfirmDeleteModal = ({
  open,
  onCancel,
  onConfirm,
  isDeleting,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}) => (
  <Modal
    isOpen={open}
    setOpen={(value) => !value && onCancel()}
    width="420px"
    padding="0px"
  >
    <div className="rounded-2xl bg-white">
      <div className="flex items-start justify-between gap-4 border-b border-[#EAECF0] px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-[#101828]">
            Delete this portfolio video?
          </h3>
          <p className="mt-1 text-sm text-[#667085]">
            This will remove the video from your public profile.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg p-1 text-[#98A2B3] hover:bg-[#F9FAFB] hover:text-[#101828]"
          aria-label="Close delete confirmation"
        >
          <X className="size-5" />
        </button>
      </div>
      <div className="flex justify-end gap-3 px-5 py-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full border border-[#E4E7EC] px-4 py-2 text-sm font-semibold text-[#344054] hover:bg-[#F9FAFB]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isDeleting}
          className="rounded-full bg-[#D92D20] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {isDeleting ? "Deleting..." : "Delete Video"}
        </button>
      </div>
    </div>
  </Modal>
);

const PortfolioVideoUploader = memo(
  ({
    files,
    setFiles,
  }: {
    files: FileDetails[];
    setFiles: Dispatch<SetStateAction<FileDetails[]>>;
  }) => (
    <VideoUploaderWithPreview
      maxFiles={1}
      useExternalState
      externalFiles={files}
      setExternalFiles={setFiles}
      showSelectedFiles
      showUploadAnimationOnly={false}
      useLiveUrl={false}
      height="140px"
      width="140px"
      cardDesignSize="h-[140px] w-[140px]"
      accept={{
        "video/mp4": [".mp4"],
        "video/webm": [],
        "video/ogg": [],
        "video/quicktime": [".mov"],
      }}
      onFilesUploaded={() => {}}
    />
  ),
);
PortfolioVideoUploader.displayName = "PortfolioVideoUploader";
const PortfolioManagerModal = ({
  isOpen,
  setOpen,
  videos,
  userId,
  showUploadInitially,
}: {
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  videos: PortfolioPreviewItem[];
  userId?: string;
  showUploadInitially: boolean;
}) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user.user);
  const [updateCreator] = useUpdateCreatorMutation();

  const [playingUrl, setPlayingUrl] = useState("");
  const [showUpload, setShowUpload] = useState(showUploadInitially);
  const [uploadedFiles, setUploadedFiles] = useState<FileDetails[]>([]);
  const [draftBrand, setDraftBrand] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [draftPersonal, setDraftPersonal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [replacingPrimary, setReplacingPrimary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PortfolioPreviewItem | null>(
    null,
  );

  const primaryVideo = getPrimaryVideo(videos);
  const uploaded = uploadedFiles[0] || null;
  const uploadReady = Boolean(uploaded?.live_url);
  const storedPortfolioVideos = useMemo(
    () =>
      ((currentUser?.portfolioVideos ?? []) as ManagedPortfolioVideoItem[])
        .filter(isValidPortfolioVideo)
        .map((video) =>
          toPersistablePortfolioVideo(video as PortfolioPreviewItem),
        ),
    [currentUser?.portfolioVideos],
  );
  const storedLegacyVideos = useMemo(
    () =>
      (currentUser?.videos ?? []).filter(
        (item) => item?.video?.live_url || item?.video?.local_url,
      ),
    [currentUser?.videos],
  );

  const getItemIdentity = (item: PortfolioPreviewItem) =>
    (item.playableUrl || getVideoId(item, 0)).trim().toLowerCase();

  const matchesItem = (
    video: ManagedPortfolioVideoItem,
    index: number,
    item: PortfolioPreviewItem,
  ) => getVideoIdentity(video, index) === getItemIdentity(item);

  const matchesLegacyItem = (
    video: TProfileVideo,
    index: number,
    item: PortfolioPreviewItem,
  ) =>
    getVideoIdentity(toLegacyPortfolioVideo(video, index), index) ===
    getItemIdentity(item);

  const matchesLegacyIdentity = (
    video: TProfileVideo,
    index: number,
    identity: string,
  ) =>
    getVideoIdentity(toLegacyPortfolioVideo(video, index), index) === identity;

  const persistVideoCollections = async (
    nextPortfolioVideos: ManagedPortfolioVideoItem[],
    nextLegacyVideos: TProfileVideo[],
    message: string,
  ) => {
    if (!userId) {
      toast.error("User information is missing");
      return false;
    }

    const previousPortfolioVideos = (currentUser?.portfolioVideos ??
      []) as ManagedPortfolioVideoItem[];
    const previousLegacyVideos = currentUser?.videos ?? [];

    dispatch(
      updateUser({ field: "portfolioVideos", value: nextPortfolioVideos }),
    );
    dispatch(updateUser({ field: "videos", value: nextLegacyVideos }));

    try {
      await updateCreator({
        id: userId,
        body: {
          portfolioVideos: nextPortfolioVideos,
          videos: nextLegacyVideos,
        },
      }).unwrap();
      toast.success(message);
      return true;
    } catch (error) {
      dispatch(
        updateUser({
          field: "portfolioVideos",
          value: previousPortfolioVideos,
        }),
      );
      dispatch(updateUser({ field: "videos", value: previousLegacyVideos }));
      toast.error("Failed to update portfolio videos. Please try again.");
      return false;
    }
  };

  const resetDraft = () => {
    setUploadedFiles([]);
    setDraftBrand("");
    setDraftDescription("");
    setDraftPersonal(false);
    setEditingId(null);
    setReplacingPrimary(false);
  };

  const handlePlay = (item: PortfolioPreviewItem) => {
    if (item.sourceType === "link") {
      window.open(item.playableUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setPlayingUrl((current) =>
      current === item.playableUrl ? "" : item.playableUrl,
    );
  };

  const setPrimary = async (item: PortfolioPreviewItem) => {
    if (saving || item.isPrimary) return;

    setSaving(true);
    const nextPortfolioVideos = storedPortfolioVideos.map((video, index) => ({
      ...video,
      isPrimary: matchesItem(video, index, item),
    }));
    const nextLegacyVideos = storedLegacyVideos.map((video, index) => ({
      ...video,
      isActive: matchesLegacyItem(video, index, item),
    }));

    await persistVideoCollections(
      nextPortfolioVideos,
      nextLegacyVideos,
      "Primary portfolio video updated.",
    );
    setSaving(false);
  };

  const startEdit = (item: PortfolioPreviewItem) => {
    setShowUpload(true);
    setEditingId(getItemIdentity(item));
    setDraftBrand(item.brandName || "");
    setDraftDescription(item.description || "");
    setDraftPersonal(Boolean(item.isPersonalProject));
    setUploadedFiles(item.file ? [item.file] : []);
  };

  const startPrimaryReplacement = (item: PortfolioPreviewItem) => {
    setShowUpload(true);
    setEditingId(getItemIdentity(item));
    setReplacingPrimary(true);
    setDraftBrand(item.brandName || "");
    setDraftDescription(item.description || "");
    setDraftPersonal(Boolean(item.isPersonalProject));
    // An empty uploader is intentional: selecting a file replaces this video
    // instead of adding another portfolio item.
    setUploadedFiles([]);
  };

  const handleSaveDraft = async () => {
    if (saving) return;
    if (!draftDescription.trim()) {
      toast.error("Please add a description or role.");
      return;
    }
    if ((!editingId || replacingPrimary) && !uploadReady) {
      toast.error("Please upload a video first.");
      return;
    }
    if (editingId && uploadedFiles.length > 0 && !uploadReady) {
      toast.error("Please wait until the upload finishes.");
      return;
    }

    setSaving(true);
    const existingRaw = storedPortfolioVideos;
    const editingLegacy = Boolean(
      editingId &&
      storedLegacyVideos.some((video, index) =>
        matchesLegacyIdentity(video, index, editingId),
      ),
    );
    const legacyItem = editingLegacy
      ? storedLegacyVideos.find((video, index) =>
          matchesLegacyIdentity(video, index, editingId as string),
        )
      : undefined;
    const hasPrimary =
      existingRaw.some((video) => video.isPrimary) ||
      videos.some((video) => video.isPrimary);

    const nextPortfolioVideos = editingId
      ? existingRaw.map((video, index) => {
          const identity = getVideoIdentity(video, index);
          if (identity !== editingId) return video;

          return {
            ...video,
            brandName: draftBrand.trim(),
            description: draftDescription.trim(),
            isPersonalProject: draftPersonal,
            sourceType: "upload" as const,
            linkUrl: undefined,
            file: uploaded || video.file,
          };
        })
      : [
          ...existingRaw,
          {
            id: crypto.randomUUID(),
            brandName: draftBrand.trim(),
            description: draftDescription.trim(),
            isPersonalProject: draftPersonal,
            sourceType: "upload" as const,
            file: uploaded as FileDetails,
            createdAt: new Date().toISOString(),
            isPrimary: !hasPrimary && videos.length === 0,
          },
        ];

    // Legacy entries cannot store portfolio metadata (including description).
    // Preserve their media record and overlay the editable metadata as a
    // portfolio item; the normalized display de-duplicates by video URL.
    if (editingId && editingLegacy && legacyItem) {
      nextPortfolioVideos.push({
        id: crypto.randomUUID(),
        brandName: draftBrand.trim(),
        description: draftDescription.trim(),
        isPersonalProject: draftPersonal,
        sourceType: "upload",
        file: uploaded || legacyItem.video,
        createdAt: new Date().toISOString(),
        isPrimary: legacyItem.isActive,
      });
    }

    const nextLegacyVideos =
      editingId && editingLegacy && replacingPrimary
        ? storedLegacyVideos.filter(
            (video, index) => !matchesLegacyIdentity(video, index, editingId),
          )
        : storedLegacyVideos;

    const success = await persistVideoCollections(
      nextPortfolioVideos,
      nextLegacyVideos,
      editingId ? "Portfolio video updated." : "Portfolio video uploaded.",
    );
    if (success) {
      resetDraft();
      setShowUpload(false);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);

    const remainingPortfolioVideos = storedPortfolioVideos.filter(
      (video, index) => !matchesItem(video, index, deleteTarget),
    );
    const remainingLegacyVideos = storedLegacyVideos.filter(
      (video, index) => !matchesLegacyItem(video, index, deleteTarget),
    );
    const removedPortfolioVideo =
      remainingPortfolioVideos.length !== storedPortfolioVideos.length;
    const removedLegacyVideo =
      remainingLegacyVideos.length !== storedLegacyVideos.length;

    if (!removedPortfolioVideo && !removedLegacyVideo) {
      toast.error("This video could not be found in your saved portfolio.");
      setSaving(false);
      return;
    }

    const remainingVideos = normalizeVideos([
      ...remainingPortfolioVideos,
      ...remainingLegacyVideos.map(toLegacyPortfolioVideo),
    ]);
    const currentPrimary = getPrimaryVideo(remainingVideos);
    const primaryIdentity = currentPrimary
      ? getItemIdentity(currentPrimary)
      : null;

    const nextPortfolioVideos = remainingPortfolioVideos.map(
      (video, index) => ({
        ...video,
        isPrimary:
          primaryIdentity !== null &&
          getVideoIdentity(video, index) === primaryIdentity,
      }),
    );
    const nextLegacyVideos = remainingLegacyVideos.map((video, index) => ({
      ...video,
      isActive:
        primaryIdentity !== null &&
        getVideoIdentity(toLegacyPortfolioVideo(video, index), index) ===
          primaryIdentity,
    }));

    const success = await persistVideoCollections(
      nextPortfolioVideos,
      nextLegacyVideos,
      "Portfolio video deleted.",
    );
    if (success) {
      setDeleteTarget(null);
      if (editingId === getItemIdentity(deleteTarget)) resetDraft();
    }
    setSaving(false);
  };

  return (
    <>
      <Modal isOpen={isOpen} setOpen={setOpen} width="920px" padding="0px">
        <section className="flex max-h-[88vh] flex-col overflow-hidden rounded-2xl bg-white">
          <div className="flex items-start justify-between gap-4 border-b border-[#EAECF0] px-6 py-5">
            <div>
              <h3 className="text-lg font-semibold text-[#101828]">
                Portfolio Videos
              </h3>
              <p className="mt-1 max-w-[620px] text-sm text-[#667085]">
                Upload, preview, delete, and choose which video appears first on
                your public profile.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1.5 text-[#98A2B3] hover:bg-[#F9FAFB] hover:text-[#101828]"
              aria-label="Close portfolio manager"
            >
              <X className="size-5" />
            </button>
          </div>

          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
            <section className="rounded-2xl border border-[#E4E7EC] bg-[#FCFCFD] p-4">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-[#101828]">
                    Primary Portfolio Video
                  </h4>
                  <p className="mt-1 text-xs text-[#667085]">
                    This video is shown first to brands visiting your profile.
                  </p>
                </div>
                {primaryVideo && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF3] px-2.5 py-1 text-xs font-semibold text-[#027A48]">
                    <Star className="size-3.5 fill-current" />
                    Primary
                  </span>
                )}
              </div>

              {primaryVideo ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px_1fr]">
                  <div className="h-[250px] overflow-hidden rounded-xl border border-[#E4E7EC] bg-black">
                    <PlayPreview
                      item={primaryVideo}
                      playingUrl={playingUrl}
                      onPlay={handlePlay}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col justify-between gap-4">
                    <div>
                      <h5 className="mt-3 text-base font-semibold text-[#101828]">
                        {primaryVideo.title}
                      </h5>
                      <p className="mt-2 text-sm leading-6 text-[#475467]">
                        {primaryVideo.description ||
                          "No description added yet."}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => startPrimaryReplacement(primaryVideo)}
                      className="w-fit rounded-full border border-[#E4E7EC] bg-white px-4 py-2 text-sm font-semibold text-[#344054] hover:bg-[#F9FAFB]"
                    >
                      Change Primary Video
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-[#D0D5DD] bg-white p-6 text-center">
                  <FileVideo className="mx-auto size-9 text-[#98A2B3]" />
                  <p className="mt-3 text-sm font-semibold text-[#101828]">
                    No primary video selected
                  </p>
                  <p className="mt-1 text-sm text-[#667085]">
                    Upload your first portfolio video to make it primary
                    automatically.
                  </p>
                </div>
              )}
            </section>

            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-[#101828]">
                    All Portfolio Videos
                  </h4>
                  <p className="mt-1 text-xs text-[#667085]">
                    {getCountLabel(videos.length)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    resetDraft();
                    setShowUpload((current) => !current);
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF6600] px-4 py-2 text-sm font-semibold text-white hover:bg-[#E65C00]"
                >
                  <Upload className="size-4" />
                  Upload New Video
                </button>
              </div>

              {showUpload && (
                <div className="rounded-2xl border border-[#EAECF0] bg-[#FAFAFA] p-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-[170px_1fr]">
                    <div className="rounded-xl border border-dashed border-[#D0D5DD] bg-white p-3">
                      <PortfolioVideoUploader
                        files={uploadedFiles}
                        setFiles={setUploadedFiles}
                      />
                      {editingId && uploadedFiles.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setUploadedFiles([])}
                          className="mt-2 w-full text-xs font-semibold text-[#475467] hover:text-[#FF6600]"
                        >
                          Replace selected video
                        </button>
                      )}
                      {uploadedFiles.length > 0 && !uploadReady && (
                        <p className="mt-2 text-center text-xs text-[#667085]">
                          Uploading, please wait...
                        </p>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
                        <input
                          type="text"
                          value={draftBrand}
                          onChange={(event) =>
                            setDraftBrand(event.target.value)
                          }
                          placeholder="Brand name or title"
                          className="h-10 rounded-xl border border-[#D0D5DD] bg-white px-3 text-sm outline-none focus:border-[#FF6600]"
                        />
                        <label className="flex h-10 items-center gap-2 rounded-xl border border-[#D0D5DD] bg-white px-3 text-sm font-medium text-[#344054]">
                          <input
                            type="checkbox"
                            checked={draftPersonal}
                            onChange={(event) =>
                              setDraftPersonal(event.target.checked)
                            }
                            className="peer sr-only"
                          />
                          <span className="flex size-4 items-center justify-center rounded border border-[#D0D5DD] bg-white text-white peer-checked:border-[#FF6600] peer-checked:bg-[#FF6600]">
                            <Check className="size-3" strokeWidth={3} />
                          </span>
                          Personal
                        </label>
                      </div>
                      <textarea
                        value={draftDescription}
                        onChange={(event) =>
                          setDraftDescription(event.target.value)
                        }
                        placeholder="Description or role"
                        rows={4}
                        className="w-full resize-none rounded-xl border border-[#D0D5DD] bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6600]"
                      />
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            resetDraft();
                            setShowUpload(false);
                          }}
                          className="rounded-full border border-[#E4E7EC] px-4 py-2 text-sm font-semibold text-[#344054] hover:bg-white"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveDraft}
                          disabled={
                            saving || (uploadedFiles.length > 0 && !uploadReady)
                          }
                          className="rounded-full bg-[#FF6600] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                        >
                          {saving
                            ? "Saving..."
                            : editingId
                              ? replacingPrimary
                                ? "Replace Primary Video"
                                : "Save Details"
                              : "Add to Portfolio"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {videos.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#D0D5DD] bg-white p-8 text-center">
                  <FileVideo className="mx-auto size-10 text-[#98A2B3]" />
                  <p className="mt-3 text-sm font-semibold text-[#101828]">
                    No portfolio videos yet
                  </p>
                  <p className="mt-1 text-sm text-[#667085]">
                    Upload your best work so brands can understand your style.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {videos.map((item) => (
                    <article
                      key={item.normalizedId}
                      className="rounded-2xl border border-[#EAECF0] bg-white p-3 shadow-sm"
                    >
                      <div className="grid grid-cols-[112px_1fr] gap-3">
                        <div className="h-[150px] overflow-hidden rounded-xl border border-[#E4E7EC] bg-black">
                          <PlayPreview
                            item={item}
                            playingUrl={playingUrl}
                            onPlay={handlePlay}
                          />
                        </div>
                        <div className="flex min-w-0 flex-col justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap gap-1.5">
                              {item.isPrimary && (
                                <span className="rounded-full bg-[#ECFDF3] px-2 py-0.5 text-[11px] font-semibold text-[#027A48]">
                                  Primary
                                </span>
                              )}
                            </div>
                            <h5 className="mt-2 truncate text-sm font-semibold text-[#101828]">
                              {item.title}
                            </h5>
                            <p className="mt-1 line-clamp-2 text-xs leading-5 text-[#667085]">
                              {item.description || "No description added yet."}
                            </p>
                            {formatDate(item.createdAt) && (
                              <p className="mt-1 text-xs text-[#98A2B3]">
                                Uploaded {formatDate(item.createdAt)}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => handlePlay(item)}
                              className="rounded-lg border border-[#E4E7EC] p-2 text-[#344054] hover:bg-[#F9FAFB]"
                              aria-label="Play portfolio video"
                            >
                              <Play className="size-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setPrimary(item)}
                              disabled={item.isPrimary || saving}
                              className="rounded-lg border border-[#E4E7EC] px-2 py-1.5 text-xs font-semibold text-[#344054] hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Set as Primary
                            </button>
                            <button
                              type="button"
                              onClick={() => startEdit(item)}
                              className="rounded-lg border border-[#E4E7EC] p-2 text-[#344054] hover:bg-[#F9FAFB]"
                              aria-label="Edit video details"
                            >
                              <Pencil className="size-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(item)}
                              className="rounded-lg border border-[#FEE4E2] bg-[#FFFBFA] p-2 text-[#D92D20] hover:bg-[#FEF3F2]"
                              aria-label="Delete portfolio video"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </section>
      </Modal>

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isDeleting={saving}
      />
    </>
  );
};

const ProfilePortfolioVideos = ({ data }: { data: TUser }) => {
  const userSlice = useAppSelector((state) => state.user.user);
  const user = (userSlice || data || {}) as TUser;
  const [isManagerOpen, setIsManagerOpen] = useState(false);
  const [openUploadFirst, setOpenUploadFirst] = useState(false);
  const [playingUrl, setPlayingUrl] = useState("");

  const videos = useMemo(
    () => getPortfolioVideos(user),
    [user?.portfolioVideos, user?.videos],
  );

  const handleProfilePlay = (item: PortfolioPreviewItem) => {
    if (item.sourceType === "link") {
      window.open(item.playableUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setPlayingUrl((current) =>
      current === item.playableUrl ? "" : item.playableUrl,
    );
  };

  const openManager = (focusUpload = false) => {
    setOpenUploadFirst(focusUpload);
    setIsManagerOpen(true);
  };

  return (
    <>
      <section className="w-full rounded-xl border border-[#EAECF0] bg-white p-5 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1.5">
            <h5 className="text-lg font-semibold leading-5">
              Portfolio Videos
            </h5>
            <p className="leading-5 text-[#475467]">
              Show brands the work that best represents your style.
            </p>
          </div>
          {videos.length > 0 && (
            <button
              type="button"
              onClick={() => openManager(false)}
              className="inline-flex h-10 w-fit items-center gap-2 rounded-full bg-[#FF6600] px-4 text-sm font-semibold text-white hover:bg-[#E65C00]"
            >
              <Upload className="size-4" />
              Manage Portfolio
            </button>
          )}
        </div>

        {videos.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#D0D5DD] bg-white p-6 text-center shadow-sm">
            <FileVideo className="mx-auto size-10 text-[#98A2B3]" />
            <p className="mt-3 text-sm font-semibold text-[#101828]">
              No portfolio videos yet
            </p>
            <p className="mx-auto mt-1 max-w-[340px] text-sm text-[#667085]">
              Upload your best work so brands can understand your style.
            </p>
            <button
              type="button"
              onClick={() => openManager(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FF6600] px-4 py-2 text-sm font-semibold text-white hover:bg-[#E65C00]"
            >
              <Upload className="size-4" />
              Upload Portfolio Video
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {videos.map((item) => (
              <PortfolioVideoCard
                key={item.normalizedId}
                item={item}
                playingUrl={playingUrl}
                onPlay={handleProfilePlay}
              />
            ))}
          </div>
        )}
      </section>

      <PortfolioManagerModal
        key={`${isManagerOpen}-${openUploadFirst}`}
        isOpen={isManagerOpen}
        setOpen={setIsManagerOpen}
        videos={videos}
        userId={user?.user_id}
        showUploadInitially={openUploadFirst}
      />
    </>
  );
};

export default ProfilePortfolioVideos;
